import React, { useState, useEffect, useRef } from "react";

const Input = React.forwardRef(
  (
    {
      className,
      type,
      label,
      error,
      success,
      maxLength,
      showCharCount = false,
      floatingLabel = false,
      glassEffect = false,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(false);
    const [charCount, setCharCount] = useState(0);
    const inputRef = useRef(null);

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current);

    useEffect(() => {
      const input = inputRef.current;
      if (input) {
        setHasValue(!!input.value);
        setCharCount(input.value?.length || 0);
      }
    }, [props.value]);

    const handleFocus = (e) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e) => {
      setIsFocused(false);
      setHasValue(!!e.target.value);
      props.onBlur?.(e);
    };

    const handleChange = (e) => {
      setHasValue(!!e.target.value);
      setCharCount(e.target.value.length);
      props.onChange?.(e);
    };

    const baseInputClasses = `
    sg-field
    ${className || ""}
  `;

    return (
      <div className="relative group">
        {/* "floatingLabel" now renders the Signal static label above the
            field; the floating animation was dropped with the old system. */}
        {floatingLabel && label && (
          <label
            htmlFor={props.id || props.name}
            className="sg-field-label"
            data-focused={isFocused || hasValue ? "true" : undefined}
          >
            {label}
          </label>
        )}

        {/* Input field */}
        <input
          ref={inputRef}
          type={type || "text"}
          className={baseInputClasses}
          aria-invalid={error ? "true" : undefined}
          data-state={error ? "error" : success ? "success" : undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          maxLength={maxLength}
          {...props}
        />

        {/* Success checkmark animation */}
        {success && !error && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sg-live">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}

        {/* Character count indicator */}
        {showCharCount && maxLength && (
          <div
            className={`
            absolute right-3 bottom-1 font-mono text-[11px] transition-colors duration-150
            ${
              charCount >= maxLength
                ? "text-sg-danger"
                : charCount >= maxLength * 0.9
                  ? "text-sg-warn"
                  : "text-sg-ink-3"
            }
          `}
          >
            {charCount}/{maxLength}
          </div>
        )}

        {typeof error === "string" && error && (
          <p className="sg-field-msg" data-tone="error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
